import { useQuery } from "react-query"
import { FC, memo, useEffect, useState } from "react"
import dynamic from "next/dynamic"
import { useRouter } from "next/router"
import { fetchProductsList } from "@/api/productsAPI"
import { ButtonToggleCompares } from "@/components/Products/parts/ButtonToggleCompares"
import { SharedText } from "@/components/SharedText/SharedText"
import { LazyHydrateContainer } from "@/hoc/LazyHydrate"
import { useCart } from "@/hooks/cart/cart"
import { useProduct } from "@/hooks/product/product"
import { useSample } from "@/hooks/sample"
import { useShippings } from "@/hooks/shippings/shippings"
import { useWatchedRecent } from "@/hooks/watchedRecent/useWatchedRecent"
import { Row } from "@/styles/utils/StyledGrid"
import { colors } from "@/styles/utils/vars"
import { Button } from "@/ui/Button/Button"
import { Modal } from "@/ui/Modal/Modal"
import { Popover } from "@/ui/Popover/Popover"
import { Typography } from "@/ui/Typography/Typography"
import type { RuleSortProduct } from "@/utils/common/helpers"
import { DescriptionProduct } from "./Description"
import { Media } from "./Media"
import { ShippingsBadges } from "./ShippingsBadges"
import {
  Body,
  Card,
  Footer,
  Header,
  Info,
  StyledDetail,
  Title,
} from "./StyledDetail"
import type { ProductDetail } from "../../../../contracts/contracts"
import { Available } from "../../Available"
import {
  AddToCartControl,
  Badges,
  ButtonToggleFavorite,
  ButtonToggleSample,
  Counter,
  Price,
  StoresAvailable,
  UnitControl,
} from "../parts"
import { StyledCodArticleInfo } from "../parts/CodeArticleInfo/StyledCodArticleInfo"
import { PopoverSimilar } from "../parts/PopoverSimilar"
import { SkeletonDetail } from "../parts/Skeleton/SkeletonDetail"
import { ControlsContainerDetail, StyledFloatControls } from "../StyledProduct"
import type { ProductType } from "../types"

const KitContentsContainer = dynamic(() =>
  import("@/components/Products/Detail/Containers/KitContentsContainer").then(
    (mod) => mod.KitContentsContainer,
  ),
)

const AnalogsContainer = dynamic(
  import("@/components/Products/Detail/Containers/AnalogsContainer").then(
    (mod) => mod.AnalogsContainer,
  ),
)

const DescriptionStatic = dynamic(() =>
  import("./DescriptionStatic").then((mod) => mod.DescriptionStatic),
)

const FindCheaperForm = dynamic(
  () =>
    import("@/components/Forms/FindCheaper").then((mod) => mod.FindCheaperForm),
  {
    ssr: false,
  },
)

const NotFindNeedForm = dynamic(
  () =>
    import("@/components/Forms/NotFindNeed").then((mod) => mod.NotFindNeedForm),
  {
    ssr: false,
  },
)

const AfterAddedToCartKit = dynamic(
  () =>
    import("../parts/AfterAddedToCartKit").then(
      (mod) => mod.AfterAddedToCartKit,
    ),
  {
    ssr: false,
  },
)

const Variations = dynamic(() =>
  import("../parts/Variations").then((mod) => mod.Variations),
)

const KitPart = dynamic(() =>
  import("../parts/KitPart").then((mod) => mod.KitPart),
)

const StoresTable = dynamic(() =>
  import("./StoresTable").then((mod) => mod.StoresTable),
)

const Properties = dynamic(() =>
  import("./Properties").then((mod) => mod.Properties),
)

export type DetailPropsType = {
  product: ProductType
  companions?: ProductDetail[]
}

export const Detail: FC<DetailPropsType> = memo(
  ({ product: productOut, companions = [] }) => {
    const router = useRouter()
    const { shareCart } = useCart()
    const {
      name,
      units,
      unitMeasure,
      updateCurrentUnit,
      totalQty,
      currentCount,
      isFetching,
      inCart,
      addToCart,
      priceCalculate,
      priceUnit,
      currentUnit,
      updateCurrentCount,
      isRemoved,
      isFetchingFavorites,
      isFavorites,
      uuid,
      isCopiedPath,
      copyPath,
      stores,
      description,
      kit,
      storesAvailable,
      images,
      availableStatus,
      isBestseller,
      isKit,
      kitParents,
      counter,
      code,
      copyCode,
      isCopiedCode,
      isCountError: isCountProductError,
      setIsCountError,
      isInit: isInitProduct,
      isAdded,
      isAvailable,
      fastQty,
      isNew,
      variations,
      isVariate,
      setIsAdded,
      isAllowSample,
      article,
      copyArticle,
      isCopiedArticle,
      isCompares,
      toggleCompares,
      share,
      sharePath,
      isShowShare,
      setIsShowShare,
    } = useProduct({
      product: productOut,
      options: {
        isSaveOnRemove: false,
        withLocation: true,
        isDetail: true,
      },
    })

    const {
      isFetching: isFetchingSample,
      toggle,
      isCountError: isCountSampleError,
      isInit: isInitSample,
      qtyInCart: qtyInCartSample,
      isDisabled: isDisabledSample,
    } = useSample({
      sample: {
        name: name || "",
        uuid: uuid || undefined,
        total_qty: productOut.total_qty,
        price: priceUnit,
        unit: unitMeasure || undefined,
      },
    })

    const { isExpressShipping, shippingDate, isDateToOnlyCompany } =
      useShippings({
        product: isInitProduct
          ? {
              fastQty: fastQty,
              currentCount: currentCount || undefined,
              totalQty: totalQty,
            }
          : undefined,
        withLocation: true,
      })

    const {
      add: addToWatchedRecent,
      exclude: excludeWatchedRecent,
      include: includeWatchedRecent,
    } = useWatchedRecent()

    const { data: kitProducts } = useQuery(
      ["products", kit],
      () =>
        fetchProductsList({
          uuids: kit.join(","),
        }),
      {
        enabled: kit.length > 0,
      },
    )

    const isInitEntity = isInitProduct && isInitSample

    const [isShowFindCheaper, setIsShowFindCheaper] = useState<boolean>(false)
    const [isShowNotFindNeed, setIsShowNotFindNeed] = useState<boolean>(false)

    const analogs: RuleSortProduct[] = [
      ...(productOut?.analogs?.fast || []),
      ...(productOut?.analogs?.other || []),
    ]

    useEffect(() => {
      const disabledShowedDialogs = () => {
        setIsAdded(false)
      }
      router.events.on("routeChangeStart", disabledShowedDialogs)
      router.events.on("routeChangeComplete", disabledShowedDialogs)

      return () => {
        router.events.off("routeChangeStart", disabledShowedDialogs)
        router.events.off("routeChangeComplete", disabledShowedDialogs)
      }
    }, [router.events])

    useEffect(() => {
      addToWatchedRecent({ uuid })
    }, [addToWatchedRecent, uuid])

    useEffect(() => {
      if (uuid) {
        // выключаем текущий товар из карусели недавно просмотренных
        excludeWatchedRecent(uuid)
      }

      return () => {
        if (uuid) {
          // включаем текущий товар в карусели, при покидании главной
          includeWatchedRecent(uuid)
        }
      }
    }, [uuid, includeWatchedRecent, excludeWatchedRecent])

    return (
      <>
        <StyledDetail>
          {!isInitEntity && <SkeletonDetail />}

          <Card data-id={uuid}>
            <Header>
              <LazyHydrateContainer>
                <Badges
                  isBestseller={isBestseller}
                  isKit={isKit}
                  isNew={isNew}
                />
              </LazyHydrateContainer>

              <Title>
                <Typography variant={"h1"}>{name}</Typography>
              </Title>

              <LazyHydrateContainer>
                <KitPart kitParents={kitParents} />
              </LazyHydrateContainer>
            </Header>

            <Body>
              <Info>
                <ShippingsBadges
                  isDateToOnlyCompany={isDateToOnlyCompany}
                  isExpressShipping={isExpressShipping}
                  shippingDate={shippingDate}
                />

                <StoresAvailable stores={storesAvailable}>
                  <Available status={availableStatus} />
                </StoresAvailable>

                {!!description && (
                  <DescriptionProduct description={description} />
                )}

                {isVariate && (
                  <LazyHydrateContainer>
                    <Variations
                      product={{
                        name: productOut.name,
                      }}
                      selected={productOut.alias}
                      items={variations}
                      limitShowBoxes={7}
                      closeMode={"hide"}
                    />
                  </LazyHydrateContainer>
                )}

                {isAvailable && (
                  <>
                    <UnitControl
                      units={units}
                      setCurrentUnit={updateCurrentUnit}
                      totalQty={totalQty}
                      unitMeasure={unitMeasure}
                      unitValueActive={currentUnit || undefined}
                      isInitProduct={isInitEntity}
                    />

                    <Row
                      style={{
                        justifyContent: "space-between",
                      }}
                    >
                      <Price
                        price={priceUnit}
                        as={"b"}
                        unitMeasure={unitMeasure}
                      />

                      <Counter
                        counter={counter}
                        currentCount={currentCount}
                        currentUnit={currentUnit}
                        initialUnit={+units[0]?.value ?? null}
                        unitMeasure={unitMeasure}
                        maxCount={totalQty}
                        isFetching={isFetching}
                        productInCart={inCart}
                        updateCountHandler={updateCurrentCount}
                        productIsRemove={isRemoved}
                        isCountError={isCountProductError || isCountSampleError}
                        setIsCountError={setIsCountError}
                        isInitProduct={isInitEntity}
                      />
                    </Row>
                  </>
                )}

                {!isAvailable && (
                  <LazyHydrateContainer>
                    <>
                      <Typography
                        color={colors.grayDark}
                        style={{
                          marginBottom: "5px",
                        }}
                      >
                        Товара нет в наличии
                      </Typography>
                      {!!priceUnit && (
                        <Typography
                          style={{
                            marginBottom: "5px",
                          }}
                        >
                          Последняя цена{" "}
                          <Price
                            price={priceUnit}
                            unitMeasure={unitMeasure}
                            variant={"span"}
                          />
                        </Typography>
                      )}
                    </>
                  </LazyHydrateContainer>
                )}

                <AddToCartControl
                  uuid={uuid}
                  isAvailable={isAvailable}
                  inCart={inCart && !isRemoved}
                  isFetching={isFetching}
                  buttonAreaContent={
                    <Price variant="total" price={priceCalculate} />
                  }
                  addToCart={addToCart}
                  variant={"detail"}
                  disabled={!totalQty}
                >
                  <ButtonToggleFavorite
                    isDetail
                    productUUID={uuid ? uuid : ""}
                    isFavorites={isFavorites}
                    isFetching={isFetchingFavorites}
                    variant={"translucent"}
                    size={"large"}
                  />
                </AddToCartControl>

                <StyledCodArticleInfo>
                  {!!code && (
                    <Popover
                      disclosure={
                        <Button onClick={copyCode} size={"small"} isClean>
                          Код: {code}
                        </Button>
                      }
                      isShow={isCopiedCode}
                      isStylingIconDisclosure={false}
                      size={"small"}
                    >
                      <Typography variant={"p12"} color={colors.green}>
                        Код скопирован!
                      </Typography>
                    </Popover>
                  )}

                  {!!article && (
                    <Popover
                      disclosure={
                        <Button onClick={copyArticle} size={"small"} isClean>
                          Арт.: {article}
                        </Button>
                      }
                      isShow={isCopiedArticle}
                      isStylingIconDisclosure={false}
                      size={"small"}
                    >
                      <Typography variant={"p12"} color={colors.green}>
                        Артикул скопирован!
                      </Typography>
                    </Popover>
                  )}
                </StyledCodArticleInfo>
              </Info>
            </Body>

            <Media images={images} name={name}>
              <StyledFloatControls>
                <ButtonToggleFavorite
                  productUUID={uuid ? uuid : ""}
                  isFavorites={isFavorites}
                  isFetching={isFetchingFavorites}
                  isClean
                  size={"small"}
                />
                <ButtonToggleCompares
                  isCompares={isCompares}
                  toggleCompares={toggleCompares}
                  isClean
                  size={"small"}
                />
                {(!!analogs.length || !!companions.length) && (
                  <PopoverSimilar
                    productUUID={uuid ? uuid : ""}
                    share={shareCart}
                    analogs={analogs}
                    companions={companions}
                    size={"small"}
                  />
                )}
              </StyledFloatControls>
            </Media>

            <Footer>
              <Row>
                <ControlsContainerDetail>
                  {isAvailable && (
                    <>
                      <ButtonToggleSample
                        isFetchingSample={isFetchingSample}
                        toggleSampleToCart={toggle}
                        sample={qtyInCartSample || 0}
                        isDisabled={isDisabledSample}
                        size={"small"}
                        isAllow={isAllowSample}
                      />
                    </>
                  )}

                  <Button
                    variant={"link"}
                    icon={"Flag"}
                    size={"small"}
                    onClick={() => {
                      setIsShowFindCheaper(true)
                    }}
                  >
                    Нашли дешевле?
                  </Button>

                  <Button
                    variant={"link"}
                    icon={"Puzzle"}
                    size={"small"}
                    onClick={() => {
                      setIsShowNotFindNeed(true)
                    }}
                  >
                    Не нашли нужного?
                  </Button>

                  <Popover
                    disclosure={
                      <Button
                        variant={"link"}
                        icon={"Link"}
                        onClick={copyPath}
                        size={"small"}
                      >
                        Ссылка
                      </Button>
                    }
                    isShow={isCopiedPath}
                    isStylingIconDisclosure={false}
                    size={"small"}
                  >
                    <Typography variant={"p12"} color={colors.green}>
                      Ссылка скопирована!
                    </Typography>
                  </Popover>

                  <Button
                    size={"small"}
                    variant={"link"}
                    icon={"Share"}
                    onClick={share}
                  >
                    Поделиться
                  </Button>
                </ControlsContainerDetail>
              </Row>
            </Footer>
          </Card>

          <LazyHydrateContainer>
            <Properties params={productOut.params || []} />
          </LazyHydrateContainer>

          <LazyHydrateContainer>
            <StoresTable stores={stores} />
          </LazyHydrateContainer>

          <LazyHydrateContainer>
            <KitContentsContainer products={kitProducts || []} />
          </LazyHydrateContainer>

          <LazyHydrateContainer>
            <DescriptionStatic />
          </LazyHydrateContainer>

          <LazyHydrateContainer>
            <AnalogsContainer analogs={analogs} />
          </LazyHydrateContainer>
        </StyledDetail>

        <Modal
          closeMode={"destroy"}
          variant={"rounded-0"}
          title={"Нашли дешевле?"}
          isShowModal={isShowFindCheaper}
          onClose={() => {
            setIsShowFindCheaper(false)
          }}
        >
          <LazyHydrateContainer>
            <FindCheaperForm uuid={uuid || undefined} />
          </LazyHydrateContainer>
        </Modal>

        <Modal
          closeMode={"destroy"}
          variant={"rounded-0"}
          title={"Не нашли нужного?"}
          isShowModal={isShowNotFindNeed}
          onClose={() => {
            setIsShowNotFindNeed(false)
          }}
        >
          <LazyHydrateContainer>
            <NotFindNeedForm uuid={uuid || undefined} />
          </LazyHydrateContainer>
        </Modal>

        {isKit && (
          <Modal
            closeMode={"destroy"}
            variant={"rounded-100"}
            isShowModal={isAdded}
            title={"Добавлен в корзину"}
          >
            <LazyHydrateContainer>
              <AfterAddedToCartKit
                product={productOut}
                companions={companions}
                kitProducts={kitProducts || []}
                analogs={analogs}
                onClose={() => {
                  setIsAdded(false)
                }}
              />
            </LazyHydrateContainer>
          </Modal>
        )}

        {kitParents.length > 0 && (
          <Modal
            closeMode={"destroy"}
            variant={"rounded-100"}
            isShowModal={isAdded}
            title={"Добавлен в корзину"}
          >
            <LazyHydrateContainer>
              <AfterAddedToCartKit
                product={productOut}
                companions={companions}
                kitParents={kitParents || []}
                analogs={analogs}
                onClose={() => {
                  setIsAdded(false)
                }}
              />
            </LazyHydrateContainer>
          </Modal>
        )}

        {!!sharePath && (
          <SharedText
            isShowed={isShowShare}
            setIsShowed={setIsShowShare}
            share={{
              url: sharePath,
              title: name ?? "",
              text: description ?? "",
            }}
          />
        )}
      </>
    )
  },
)

Detail.displayName = "Detail"
